import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { proDesignTokens } from './ProDesignTokens';
import { logAnalyticsEvent } from '../../utils/analytics';
import { r } from '../../theme/tokens';

/**
 * Styled banner with Apple-inspired design
 */
const StyledAlert = styled(Alert)(({ theme }) => ({
  borderRadius: proDesignTokens.borderRadius.large,
  border: `${r(1)} solid ${proDesignTokens.colors.pro.accentLight}`,
  backgroundColor: proDesignTokens.colors.background.card,
  boxShadow: proDesignTokens.shadows.banner,
  margin: proDesignTokens.spacing.medium,
  marginBottom: proDesignTokens.spacing.large,
  '& .MuiAlert-message': {
    width: '100%',
    padding: 0
  },
  '& .MuiAlert-action': {
    padding: 0,
    marginRight: 0
  }
}));

const BannerContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: proDesignTokens.spacing.medium,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: proDesignTokens.spacing.small
  }
}));

const MessageSection = styled(Box)(({ theme }) => ({
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    width: '100%'
  }
}));

const ActionSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: proDesignTokens.spacing.small,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'space-between'
  }
}));

export default function TrialExpiredBanner({ onDismiss }) {
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { hasSubscriptionHistory } = useFeatureAccess();

  const handleUpgrade = async () => {
    if (!currentUser) return;

    logAnalyticsEvent('trial_expired_banner_upgrade_clicked', {
      source: 'trial_expired_banner',
      user_type: hasSubscriptionHistory ? 'lapsed_subscriber' : 'trial_expired'
    });

    navigate('/subscribe', {
      state: {
        from: { pathname: window.location.pathname },
        source: 'trial_expired_banner',
      },
    });
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) {
      onDismiss();
    }

    logAnalyticsEvent('trial_expired_banner_dismissed', {
      source: 'trial_expired_banner',
      user_type: hasSubscriptionHistory ? 'lapsed_subscriber' : 'trial_expired'
    });
  };

  // Get content based on user type
  const getBannerContent = () => {
    if (hasSubscriptionHistory) {
      // User had a paid subscription that lapsed
      return {
        title: "Welcome back, your membership is ready to reactivate",
        body: "Your stacks are saved, reactivate to continue using advanced features",
        ctaText: "Reactivate Membership"
      };
    } else {
      // User had a free trial that expired
      return {
        title: "Your trial has ended",
        body: "Your stacks are saved, upgrade to unlock the advanced features",
        ctaText: "Upgrade Now"
      };
    }
  };

  const bannerContent = getBannerContent();

  if (dismissed) {
    return null;
  }

  return (
    <StyledAlert 
      severity="info" 
      icon={false}
      action={
        <IconButton
          size="small"
          onClick={handleDismiss}
          sx={{ 
            color: proDesignTokens.colors.neutral[500],
            '&:hover': {
              backgroundColor: proDesignTokens.colors.neutral[100]
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    >
      <BannerContent>
        <MessageSection>
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 600,
              color: proDesignTokens.colors.neutral[900],
              mb: 0.5
            }}
          >
            {bannerContent.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: proDesignTokens.colors.neutral[600],
              lineHeight: 1.4
            }}
          >
            {bannerContent.body}
          </Typography>
        </MessageSection>
        
        <ActionSection>
          <Button
            variant="contained"
            size="medium"
            onClick={handleUpgrade}
            sx={{
              ...proDesignTokens.components.primaryButton,
              minWidth: 140,
              height: 40
            }}
          >
            {bannerContent.ctaText}
          </Button>
        </ActionSection>
      </BannerContent>
    </StyledAlert>
  );
} 